import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import { mapActions, mapState } from "pinia";
import eventBus from "../../../utilities/eventBus";
import { AvaInitialsType } from "../../../enums/enums";
import { ProfileStatus, ProfileStatusEnum } from "../../../enums/ChatUserProfileEnum";
import { enumToMap } from "../../../common/vue-helpers/modifiers/enum-modifiers";
import { formatDate, formatDateInput } from "../../../common/vue-helpers/modifiers/value-modifier";
import { formatUserInitials } from "../../../common/vue-helpers/modifiers/format-message";
import { getStartTimeForTimePicker } from "../../../common/vue-helpers/modifiers/format-time";
import {
    ICurrUserProfileDetails,
    ISetUserProfile
} from "../../../resources-vue/vue-interfaces/messaging-service/i-user";

import UpdateUserProfilePic from "../../vue-dialogs/UpdateUserProfilePic/UpdateUserProfilePic.vue";
import { useApp } from "../../../pages-vue/App/useApp";
import { useUserStore } from "../../../pages-vue/MessageCenter/store/useUsersStore";
import { SOLUTION_COLORS } from "../../../common/vue-helpers/solution-color-index";
import { MessageService } from "../../../services/message-service";

import AvaInitials from "../../../pages-vue/MessageCenter/components/AvaInitials/AvaInitials.vue";
import TimePicker from "../../vue-custom-elements/TimePicker/TimePicker.vue";
import DatePicker from "../../vue-custom-elements/DatePicker/DatePicker.vue";

export default defineComponent({
    components: {
        DatePicker,
        TimePicker,
        AvaInitials
    },

    data() {
        return {
            _messageService: null,
            showMenu: false as boolean,
            existingStatusMsg: null as string,
            userDetails: null as ICurrUserProfileDetails,
            userStatusMsg: null,
            userStatusMsgTimer: 1 as number,
            isListening: false,
            activeMenu: null, // profile | status |statusMsg
            dateFrom: moment().format("MM/DD/YY") as string,
            customStatusMsgTime: "" as string,
            circles: SOLUTION_COLORS,
            dialogRef: null,
            ProfileStatusEnums: ProfileStatus,
            ProfileStatusEnum,
            AvaInitialsType,
            formatDate,
            formatDateInput,
            enumToMap
        };
    },

    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),
        computedMinTime() {
            const time = getStartTimeForTimePicker(this.dateFrom);
            if (time) {
                this.customStatusMsgTime = time;
            } else {
                this.customStatusMsgTime = "12:00 AM";
            }
            return time;
        }
    },

    watch: {
        showMenu: {
            handler() {
                if (!this.isListening) {
                    document.addEventListener("mousedown", this.handleClickOutside);
                    this.isListening = true;
                } else {
                    document.removeEventListener("mousedown", this.handleClickOutside);
                    this.isListening = false;
                    this.activeMenu = null;
                    this.userStatusMsgTimer = 1;
                    this.customStatusMsgTime = "";
                }
            }
        }
    },

    emits: ["update:openAgencyNav", "update:showPatients", "update:showEmployees"],

    async created() {
        this._messageService = Container.instance.get(MessageService);
    },

    async mounted() {
        await this.getCurrUserStatus();
        eventBus.on("updateUserStatus", this.getCurrUserStatus);
    },

    methods: {
        ...mapActions(useApp, ["setLoggedInUserDetails"]),
        ...mapActions(useUserStore, ["upsertUsers", "setStatusManually"]),

        circleStyle(circle: any) {
            return {
                background: `conic-gradient(${circle.light} 0deg 180deg, ${circle.dark} 180deg 360deg)`
            };
        },

        async getCurrUserStatus() {
            const data = await this._messageService.getCurrUserProfileDetails();
            const userData = {
                loginId: data.userStatus.loginId,
                profileColor: data.userStatus.profileColor,
                profilePicId: data.userStatus.profilePicId,
                currentStatus: data.userStatus.status,
                statusMessage: data.userStatus.statusMessage
            };
            this.setLoggedInUserDetails({
                ...this.loggedInUserDetails,
                profileColor: data.userStatus.profileColor,
                profilePicId: data.userStatus.profilePicId,
                currentStatus: data.userStatus.status,
                statusMessage: data.userStatus.statusMessage,
                statusMessageTimer: data.userStatus.statusMessageTimer
            });

            this.upsertUsers([userData]);
            if (data.userStatus.statusMessage.length) {
                this.existingStatusMsg = data.userStatus.statusMessage;
                this.userStatusMsg = data.userStatus.statusMessage;
            }
            this.userDetails = data.userStatus;
        },

        backToRoot() {
            this.activeMenu = null;
            this.showMenu = true;
        },

        toggleMenu() {
            this.showMenu = !this.showMenu;
        },

        openViewProfile() {
            this.activeMenu = "profile";
        },

        openSetStatus() {
            this.activeMenu = "status";
        },

        openSetStatusMsg() {
            this.activeMenu = "statusMsg";
        },

        handleClickOutside(e: any) {
            const profile = this.$refs.profileRef as any;
            if (profile && profile.contains(e.target)) return;
            if (e.target.closest(".ui-timepicker-container")) return;
            this.isListening = true;
            this.activeMenu = null;
            this.showMenu = false;
        },

        async updateProfileColor(index: number) {
            this.userDetails.profileColor = index + 1;
            this.setLoggedInUserDetails({ ...this.loggedInUserDetails, profileColor: index + 1 });
            this.setStatusManually(this.userDetails.loginId, ["profileColor", index + 1]);
            const payload: ISetUserProfile = { login: this.userDetails.loginId, profileColor: index + 1 };
            await this._messageService.updateUserProfile(payload);
        },

        async updateStatus(value: number) {
            this.userDetails.status = value;
            this.setLoggedInUserDetails({ ...this.loggedInUserDetails, currentStatus: value });
            this.setStatusManually(this.userDetails.loginId, ["currentStatus", value]);
            const payload: ISetUserProfile = { login: this.userDetails.loginId, status: value };
            await this._messageService.updateUserProfile(payload);
        },

        changeDate(date: any) {
            this.dateFrom = date;
        },

        updateStatusMsgTime(time: any) {
            this.customStatusMsgTime = time;
        },

        calculateStatusMsgTime() {
            const now = moment();
            const timerMap: Record<number, moment.Moment> = {
                1: moment("9999-12-31", "YYYY-MM-DD hh:mm A"), // max future date
                2: now.clone().endOf("day"),
                3: now.clone().add(1, "hour"),
                4: now.clone().add(4, "hours"),
                5: now.clone().endOf("week"),
                6: moment(
                    `${moment(this.dateFrom).format("YYYY-MM-DD")} ${this.customStatusMsgTime}`,
                    "YYYY-MM-DD hh:mm A"
                )
            };
            return (timerMap[this.userStatusMsgTimer] || now).toISOString(true);
        },

        async updateStatusMsg() {
            let payload: ISetUserProfile = {
                login: this.userDetails.loginId,
                statusMessage: this.userStatusMsg
            };
            let timer: string = "";
            this.existingStatusMsg = this.userStatusMsg;
            if (this.userStatusMsgTimer) {
                timer = this.calculateStatusMsgTime();
                payload.statusMessageTimer = timer;
                this.userDetails.statusMsgTimer = timer;
            }
            this.userDetails.statusMessage = this.userStatusMsg;
            this.setLoggedInUserDetails({
                ...this.loggedInUserDetails,
                statusMessage: this.userStatusMsg,
                statusMessageTimer: timer
            });
            this.setStatusManually(this.userDetails.loginId, ["statusMessage", this.userStatusMsg]);
            this.activeMenu = null;
            this.showMenu = false;
            await this._messageService.updateUserProfile(payload);
        },

        async resetStatusMsg() {
            this.userStatusMsg = "";
            this.userStatusMsgTimer = 1;
            this.existingStatusMsg = null;
            let payload: ISetUserProfile = {
                login: this.userDetails.loginId,
                statusMessage: this.userStatusMsg,
                statusMessageTimer: null
            };
            this.userDetails.statusMessage = this.userStatusMsg;
            this.setLoggedInUserDetails({
                ...this.loggedInUserDetails,
                statusMessage: this.userStatusMsg,
                statusMessageTimer: null
            });
            this.setStatusManually(this.userDetails.loginId, ["statusMessage", this.userStatusMsg]);
            await this._messageService.updateUserProfile(payload);
        },

        openProfilePicDialog() {
            this.isListening = true;
            this.activeMenu = null;
            this.showMenu = false;
            this.dialogRef = this.$dialog.open(UpdateUserProfilePic, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },

                data: {
                    currentProfilePic: this.userDetails.profilePicId,
                    userInitials: formatUserInitials(this.userDetails.userName),
                    profileColor: this.userDetails.profileColor,
                    loginId: this.userDetails.loginId
                },

                onClose: async (options: any) => {
                    if (
                        options?.data?.status === "success" &&
                        this.userDetails.profilePicId !== options.data?.assetId
                    ) {
                        this.userDetails.profilePicId = options.data.assetId;
                        this.setLoggedInUserDetails({
                            ...this.loggedInUserDetails,
                            profilePicId: options.data.assetId
                        });
                        this.setStatusManually(this.userDetails.loginId, ["profilePicId", options.data.assetId]);
                        const payload: ISetUserProfile = {
                            login: this.userDetails.loginId,
                            profilePicId: options.data.assetId
                        };
                        await this._messageService.updateUserProfile(payload);
                    }
                }
            });
        }
    },

    beforeUnmount() {
        if (this.isListening) {
            document.removeEventListener("mousedown", this.handleClickOutside);
        }
    }
});
